import React, { useRef, useEffect, useState } from 'react';
import './App.css';
import './HealthBar.css';
import CombatResultModal from './CombatResultModal';
import BeltPromotionModal from './BeltPromotionModal';
import PosePreloader from './PosePreloader';
import usePoseLoader from './hooks/usePoseLoader';
import { useEquipmentImages } from './hooks/useEquipmentImages';
import useGameScreen from './hooks/useGameScreen';
import { OpponentBehavior } from './OpponentAI';
import { MartialBehavior, MartialStyles } from './MartialOpponentAI';
import { drawOptimizedSkeleton, drawOptimizedGloves, calculateShoulderDistance } from './utils/skeletonUtils';


function App({ userData, opponentData, challengeId, onBackToMenu }) {
  console.log('App userData:', userData);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const timeLeftRef = useRef(60);
  const [countdown, setCountdown] = useState(3);
  const [isReady, setIsReady] = useState(false);
  const [gameState, setGameState] = useState('playing');
  const [opponentHealth, setOpponentHealth] = useState(100);
  const [playerHealth, setPlayerHealth] = useState(100);
  const playerHealthRef = useRef(100);
  const opponentHealthRef = useRef(100);
  const opponentMaxHealth = useRef(100);
  const [hitEffect, setHitEffect] = useState(false);
  const [damageAnimation, setDamageAnimation] = useState({ player: null, opponent: null });
  const [comboCount, setComboCount] = useState(0);
  const comboTimer = useRef(null);
  const [feedback, setFeedback] = useState({ show: false, text: '', type: '' });
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showBeltModal, setShowBeltModal] = useState(false);
  const [beltPromotionData, setBeltPromotionData] = useState(null);
  const [combatResult, setCombatResult] = useState(null);
  const [particles, setParticles] = useState([]);
  const particlesRef = useRef([]);
  const [opponentX, setOpponentX] = useState(0.3);
  const [opponentBlocking, setOpponentBlocking] = useState(false);
  const [targetZone, setTargetZone] = useState('cara-derecha');
  const targetZoneRef = useRef('cara-derecha');
  const [targetCircles, setTargetCircles] = useState([]);
  const targetCirclesRef = useRef([]);
  const circleTimerRef = useRef(null);
  const isGeneratingCircles = useRef(false);
  const opponentDirection = useRef(1);
  const opponentSpeed = useRef(0.015);
  const opponentXRef = useRef(0.3);
  const equipmentImages = useEquipmentImages();
  const opponentAI = useRef(null);
  const playerAttackState = useRef({ isAttacking: false, lastAttackTime: 0 });
  const [isGrabbed, setIsGrabbed] = useState(false);
  const [grabEscapeProgress, setGrabEscapeProgress] = useState(0);
  const grabRef = useRef({ active: false, startTime: 0, shakeCount: 0, shakeNeeded: 6, lastDir: 0 });
  const audioRef = useRef(null);
  const golpeOponenteRef = useRef(null);
  const pegarOponenteRef = useRef(null);
  const combatEndedRef = useRef(false);
  const onResultsRef = useRef(null);
  
  useGameScreen();

  const zones = {
    'cara-derecha': { img: '/lado/ladacaraderecho.jpg', name: 'Cara Derecha', y: 0.40, xOffset: -0.06, color: '#888888' },
    'cara-izquierda': { img: '/lado/ladacaraizquierda.jpg', name: 'Cara Izquierda', y: 0.40, xOffset: 0.06, color: '#888888' },
    'cuerpo-derecho': { img: '/lado/ladoderecho.jpg', name: 'Cuerpo Derecho', y: 0.57, xOffset: -0.05, color: '#888888' },
    'cuerpo-centro': { img: '/lado/ladocentro.jpg', name: 'Cuerpo Centro', y: 0.57, xOffset: 0, color: '#888888' },
    'cuerpo-izquierdo': { img: '/lado/ladoizquierdo.jpg', name: 'Cuerpo Izquierdo', y: 0.57, xOffset: 0.04, color: '#888888' }
  };
  
  const kickCount = useRef(0);
  const prevHand = useRef({ r: [0, 0], l: [0, 0] });
  const prevFoot = useRef({ r: [0, 0], l: [0, 0] });
  const attack = useRef({ active: false, x: 0.5, y: 0.3, size: 50, startTime: 0, type: 'hand' });
  const lastHitTime = useRef(0);
  
  // Sistema de intención de golpe/patada
  const strikeState = useRef({
    rightHand: { phase: 'idle', startTime: 0, extension: 0, direction: 0 },
    leftHand: { phase: 'idle', startTime: 0, extension: 0, direction: 0 },
    rightFoot: { phase: 'idle', startTime: 0, extension: 0, direction: 0 },
    leftFoot: { phase: 'idle', startTime: 0, extension: 0, direction: 0 }
  });
  
  // Cargar imágenes
  const images = useRef({
    opponent: new Image(),
    opponentReverse: new Image(),
    opponentAttack: new Image(),
    opponentAtackPie: new Image(),
    opponentAttack2: new Image(),
    opponentAtackPie2: new Image(),
    attackGlove: new Image(),
    pieAtack: new Image(),
    attackGlove2: new Image(),
    pieAtack2: new Image(),
    opponentGrab: new Image(),
    gloveHandDerecha: new Image(),
    gloveHandIzquierda: new Image(),
    gloveFoot: new Image(),
    playerHead: new Image(),
    playerBody: new Image(),
    background: new Image(),
    armImage: new Image(),
    leftArmImage: new Image(),
    rightLegImage: new Image(),
    leftLegImage: new Image()
  });

  useEffect(() => {
    let loadedCount = 0;
    const totalImages = 11;
    
    const onImageLoad = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        setImagesLoaded(true);
      }
    };
    
    // Las imágenes del oponente se cargan en el useEffect anterior
    images.current.opponent.onload = onImageLoad;
    images.current.opponent.onerror = onImageLoad;
    
    images.current.opponentReverse.onload = onImageLoad;
    images.current.opponentReverse.onerror = onImageLoad;
    
    images.current.opponentAttack.onload = onImageLoad;
    images.current.opponentAttack.onerror = onImageLoad;
    
    images.current.opponentAtackPie.onload = onImageLoad;
    images.current.opponentAtackPie.onerror = onImageLoad;
    
    // Cargar imágenes de equipamiento dinámicamente
    if (!equipmentImages.loading) {
      images.current.gloveHandDerecha.src = equipmentImages.gloveHandDerecha;
      images.current.gloveHandDerecha.onload = onImageLoad;
      images.current.gloveHandDerecha.onerror = onImageLoad;
      
      images.current.gloveHandIzquierda.src = equipmentImages.gloveHandIzquierda;
      images.current.gloveHandIzquierda.onload = onImageLoad;
      images.current.gloveHandIzquierda.onerror = onImageLoad;
      
      images.current.gloveFoot.src = equipmentImages.gloveFoot;
      images.current.gloveFoot.onload = onImageLoad;
      images.current.gloveFoot.onerror = onImageLoad;
      
      images.current.playerHead.src = equipmentImages.playerHead;
      images.current.playerHead.onload = onImageLoad;
      images.current.playerHead.onerror = onImageLoad;
    }
    
    // Las imágenes de ataque se cargan en el useEffect anterior
    images.current.attackGlove.onload = onImageLoad;
    images.current.attackGlove.onerror = onImageLoad;
    
    images.current.pieAtack.onload = onImageLoad;
    images.current.pieAtack.onerror = onImageLoad;
    
    images.current.background.src = opponentData?.id?.startsWith('martial_') ? '/img/backgroundtorneomarcial.png' : '/img/background.png';
    images.current.background.onload = onImageLoad;
    images.current.background.onerror = onImageLoad;
    
    images.current.playerBody.src = '/img/rotura/traje.png';
    images.current.armImage.src = '/img/rotura/brazoderecho.png';
    images.current.leftArmImage.src = '/img/rotura/brazoizquierdo.png';
    images.current.rightLegImage.src = '/img/rotura/piederecho.png';
    images.current.leftLegImage.src = '/img/rotura/pieizquierdo.png';
  }, [equipmentImages, opponentData]);

  // Inicializar IA del oponente y cargar imágenes específicas
  useEffect(() => {
    if (opponentData?.id) {
      // Detectar si es torneo marcial
      const isMartial = opponentData.id.startsWith('martial_');
      
      if (isMartial) {
        const martialIndex = opponentData.martialIndex || 0;
        opponentAI.current = new MartialBehavior(martialIndex);
        const initialHealth = opponentAI.current.getInitialHealth();
        setOpponentHealth(initialHealth);
        opponentHealthRef.current = initialHealth;
        opponentMaxHealth.current = initialHealth;
        
        const style = MartialStyles[martialIndex];
        const folder = `/torneomartiarl/${style.folder}`;
        images.current.opponent.src = `${folder}/guardia.png`;
        images.current.opponentReverse.src = `${folder}/guardia.png`;
        // Pose del oponente cuando ataca
        images.current.opponentAttack.src = `${folder}/golpe1mano.png`;
        images.current.opponentAtackPie.src = `${folder}/golpe1pie.png`;
        images.current.opponentAttack2.src = `${folder}/golpe2mano.png`;
        images.current.opponentAtackPie2.src = `${folder}/golpe2pie.png`;
        // Proyectil que viene hacia el jugador
        images.current.attackGlove.src = `${folder}/ataquegolpe1mano.png`;
        images.current.pieAtack.src = `${folder}/ataquegolpe1pie.png`;
        images.current.attackGlove2.src = `${folder}/ataquegolpe2mano.png`;
        images.current.pieAtack2.src = `${folder}/ataquegolpe2pie.png`;
        images.current.opponentGrab.src = `${folder}/agarre.png`;
      } else {
        opponentAI.current = new OpponentBehavior(opponentData.id);
        const initialHealth = opponentAI.current.getInitialHealth();
        setOpponentHealth(initialHealth);
        opponentHealthRef.current = initialHealth;
        opponentMaxHealth.current = initialHealth;
        
        const imgFolder = opponentAI.current.getImageFolder();
        images.current.opponent.src = `/img/${imgFolder}/opponent.png`;
        images.current.opponentReverse.src = `/img/${imgFolder}/opponentreverse.png`;
        images.current.opponentAttack.src = `/img/${imgFolder}/opponentatack.png`;
        images.current.opponentAtackPie.src = `/img/${imgFolder}/opponentatackpie.png`;
        images.current.attackGlove.src = `/img/${imgFolder}/attack-glove.png`;
        images.current.pieAtack.src = `/img/${imgFolder}/pieatack.png`;
      }
    }
  }, [opponentData]);

  // Sistema de partículas
  const createParticles = (x, y, color, count = 20) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 8,
        vy: (Math.random() - 0.5) * 8,
        life: 1,
        color,
        size: Math.random() * 4 + 2
      });
    }
    particlesRef.current = [...particlesRef.current, ...newParticles];
  };

  const updateParticles = () => {
    particlesRef.current = particlesRef.current.filter(p => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.3; // gravedad
      p.life -= 0.02;
      return p.life > 0;
    });
  };

  const drawParticles = (ctx) => {
    particlesRef.current.forEach(p => {
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
  };

  const drawOpponent = (ctx, width, height) => {
    const opponentXPos = width * opponentXRef.current;
    const opponentY = height * 0.48;
    
    let opponentImg;
    if (grabRef.current.active) {
      opponentImg = images.current.opponentGrab;
    } else if (attack.current.active) {
      if (attack.current.type === 'foot') {
        opponentImg = attack.current.variant === 2 ? images.current.opponentAtackPie2 : images.current.opponentAtackPie;
      } else {
        opponentImg = attack.current.variant === 2 ? images.current.opponentAttack2 : images.current.opponentAttack;
      }
    } else {
      opponentImg = opponentDirection.current > 0 ? images.current.opponent : images.current.opponentReverse;
    }
    
    if (opponentImg.complete && opponentImg.naturalWidth > 0) {
      const imgWidth = 250;
      const imgHeight = 375;
      ctx.drawImage(opponentImg, opponentXPos - imgWidth/2, opponentY - 150, imgWidth, imgHeight);
    } else {
      ctx.fillStyle = '#8B4513';
      ctx.beginPath();
      ctx.arc(opponentXPos, opponentY, 80, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.fillStyle = '#FF4444';
      ctx.fillRect(opponentXPos - 70, opponentY + 60, 140, 160);
      ctx.fillRect(opponentXPos - 100, opponentY + 70, 40, 110);
      ctx.fillRect(opponentXPos + 60, opponentY + 70, 40, 110);
    }
    
    const time = Date.now() / 1000;
    
    // Dibujar círculos objetivo múltiples
    targetCirclesRef.current.forEach((circle, index) => {
      const zoneX = opponentXPos + (width * circle.xOffset);
      const zoneY = height * circle.y;
      const radius = circle.radius || 40;
      
      // Calcular tiempo restante para este círculo
      const elapsed = Date.now() - circle.startTime;
      const timeLeft = Math.max(0, circle.duration - elapsed);
      const progress = 1 - (timeLeft / circle.duration);
      const isExpired = timeLeft === 0;
      
      if (isExpired) return; // No dibujar círculos expirados
      
      // Todos los círculos son activos (pueden ser golpeados)
      const pulse = Math.sin(time * 5) * 0.3 + 0.7;
      const glow = Math.sin(time * 4) * 8 + 12;
      const rotation = time * 2;
      
      // Anillo exterior giratorio
      ctx.save();
      ctx.translate(zoneX, zoneY);
      ctx.rotate(rotation);
      ctx.strokeStyle = `rgba(255, 0, 0, ${pulse * 0.6})`;
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 10]);
      ctx.beginPath();
      ctx.arc(0, 0, radius + 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      
      // Círculo de progreso (tiempo)
      const gradient = ctx.createRadialGradient(zoneX, zoneY, 0, zoneX, zoneY, radius + 8);
      if (progress > 0.7) {
        gradient.addColorStop(0, '#FF0000');
        gradient.addColorStop(1, '#8B0000');
      } else {
        gradient.addColorStop(0, '#FFD700');
        gradient.addColorStop(1, '#FF6B00');
      }
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(zoneX, zoneY, radius + 8, -Math.PI / 2, -Math.PI / 2 + (2 * Math.PI * progress), false);
      ctx.stroke();
      
      // Círculo principal con glow
      ctx.shadowBlur = 30 + glow;
      ctx.shadowColor = '#FF0000';
      const mainGradient = ctx.createRadialGradient(zoneX, zoneY, 0, zoneX, zoneY, radius);
      mainGradient.addColorStop(0, `rgba(255, 0, 0, ${pulse * 0.3})`);
      mainGradient.addColorStop(0.7, `rgba(255, 0, 0, ${pulse * 0.6})`);
      mainGradient.addColorStop(1, `rgba(255, 0, 0, ${pulse})`);
      ctx.strokeStyle = mainGradient;
      ctx.lineWidth = 8;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(zoneX, zoneY, radius + Math.sin(time * 6) * 4, 0, 2 * Math.PI);
      ctx.stroke();
      
      // Círculo interior
      ctx.strokeStyle = `rgba(255, 68, 68, ${pulse * 0.8})`;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(zoneX, zoneY, radius - 10, 0, 2 * Math.PI);
      ctx.stroke();
      
      ctx.shadowBlur = 0;
      
      // Número en el centro (sin invertir)
      ctx.save();
      ctx.scale(-1, 1);
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 8;
      ctx.fillText((index + 1).toString(), -zoneX, zoneY);
      ctx.shadowBlur = 0;
      ctx.restore();
    });
  };

  const drawSkeleton = (ctx, landmarks, width, height) => {
    return drawOptimizedSkeleton(ctx, landmarks, width, height, images.current);
  };

  const drawGloves = (ctx, landmarks, width, height) => {
    if (!landmarks) return;
    const shoulderDist = calculateShoulderDistance(landmarks, width);
    drawOptimizedGloves(ctx, landmarks, width, height, images.current, shoulderDist);
  };

  const drawAttack = (ctx, width, height) => {
    if (!attack.current.active) return;
    
    const elapsed = Date.now() - attack.current.startTime;
    const progress = elapsed / 2000;
    
    if (progress >= 1) {
      attack.current.active = false;
      return;
    }
    
    const attackX = width * attack.current.x;
    const attackY = height * (0.3 + progress * 0.4);
    const attackSize = attack.current.size + progress * 150;
    
    // Dibujar imagen del guante o pie atacante
    const attackImg = attack.current.type === 'foot' 
      ? (attack.current.variant === 2 ? images.current.pieAtack2 : images.current.pieAtack)
      : (attack.current.variant === 2 ? images.current.attackGlove2 : images.current.attackGlove);
    if (attackImg.complete && attackImg.naturalWidth > 0) {
      ctx.drawImage(attackImg, attackX - attackSize/2, attackY - attackSize/2, attackSize, attackSize);
    } else {
      ctx.fillStyle = '#8B0000';
      ctx.beginPath();
      ctx.arc(attackX, attackY, attackSize/2, 0, 2 * Math.PI);
      ctx.fill();
    }
    
    // Advertencia
    if (progress < 0.3) {
      ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
      ctx.fillRect(0, 0, width, height);
      ctx.save();
      ctx.scale(-1, 1);
      ctx.fillStyle = '#FFF';
      ctx.font = 'bold 60px Arial';
      const message = attack.current.type === 'foot' ? '¡BLOQUEA!' : '¡ESQUIVA!';
      const xPos = attack.current.type === 'foot' ? -width / 2 - 130 : -width / 2 - 120;
      ctx.fillText(message, xPos, height / 2);
      ctx.restore();
    }
  };

  const detectHit = (landmarks, width, height) => {
    if (!landmarks) return;
    
    const rIndex = landmarks[20];
    const lIndex = landmarks[19];
    const rFoot = landmarks[32];
    const lFoot = landmarks[31];
    const nose = landmarks[0];
    const rShoulder = landmarks[12];
    const lShoulder = landmarks[11];
    const rElbow = landmarks[14];
    const lElbow = landmarks[13];
    const rHip = landmarks[24];
    const lHip = landmarks[23];
    const rKnee = landmarks[26];
    const lKnee = landmarks[25];
    
    // Detectar escape del agarre sacudiendo brazos
    if (grabRef.current.active) {
      const handR = [rIndex.x, rIndex.y];
      const handL = [lIndex.x, lIndex.y];
      const avgHandX = (handR[0] + handL[0]) / 2;
      const speedHands = Math.abs(handR[0] - prevHand.current.r[0]) + Math.abs(handL[0] - prevHand.current.l[0]);
      
      // Detectar cambio de dirección lateral rápido (sacudida)
      if (speedHands > 0.03) {
        const currentDir = (handR[0] - prevHand.current.r[0]) > 0 ? 1 : -1;
        if (currentDir !== grabRef.current.lastDir && grabRef.current.lastDir !== 0) {
          grabRef.current.shakeCount++;
          const progress = (grabRef.current.shakeCount / grabRef.current.shakeNeeded) * 100;
          setGrabEscapeProgress(progress);
          
          if (grabRef.current.shakeCount >= grabRef.current.shakeNeeded) {
            grabRef.current.active = false;
            setIsGrabbed(false);
            setFeedback({ show: true, text: '¡ESCAPASTE!', type: 'defense' });
            setTimeout(() => setFeedback({ show: false, text: '', type: '' }), 800);
          }
        }
        grabRef.current.lastDir = currentDir;
      }
      
      prevHand.current = { r: handR, l: handL };
      prevFoot.current = { r: [rFoot.x, rFoot.y], l: [lFoot.x, lFoot.y] };
      return; // No detectar golpes durante agarre
    }
    
    const opponentXPos = width * opponentXRef.current;
    const zone = zones[targetZone];
    const zoneX = opponentXPos + (width * zone.xOffset);
    const zoneY = height * zone.y;
    
    // Calcular extensión de brazos (0 = retraído, 1 = extendido)
    const rArmLength = Math.sqrt((rIndex.x - rShoulder.x) ** 2 + (rIndex.y - rShoulder.y) ** 2);
    const lArmLength = Math.sqrt((lIndex.x - lShoulder.x) ** 2 + (lIndex.y - lShoulder.y) ** 2);
    const rElbowDist = Math.sqrt((rElbow.x - rShoulder.x) ** 2 + (rElbow.y - rShoulder.y) ** 2);
    const lElbowDist = Math.sqrt((lElbow.x - lShoulder.x) ** 2 + (lElbow.y - lShoulder.y) ** 2);
    const rArmExtension = rArmLength / (rElbowDist * 2);
    const lArmExtension = lArmLength / (lElbowDist * 2);
    
    // Calcular extensión de piernas
    const rLegLength = Math.sqrt((rFoot.x - rHip.x) ** 2 + (rFoot.y - rHip.y) ** 2);
    const lLegLength = Math.sqrt((lFoot.x - lHip.x) ** 2 + (lFoot.y - lHip.y) ** 2);
    const rKneeDist = Math.sqrt((rKnee.x - rHip.x) ** 2 + (rKnee.y - rHip.y) ** 2);
    const lKneeDist = Math.sqrt((lKnee.x - lHip.x) ** 2 + (lKnee.y - lHip.y) ** 2);
    const rLegExtension = rLegLength / (rKneeDist * 2);
    const lLegExtension = lLegLength / (lKneeDist * 2);
    
    // Dirección del movimiento (hacia adelante = positivo)
    const handR = [rIndex.x, rIndex.y];
    const handL = [lIndex.x, lIndex.y];
    const footR = [rFoot.x, rFoot.y];
    const footL = [lFoot.x, lFoot.y];
    
    const rHandDirX = handR[0] - prevHand.current.r[0];
    const lHandDirX = handL[0] - prevHand.current.l[0];
    const rFootDirY = footR[1] - prevFoot.current.r[1];
    const lFootDirY = footL[1] - prevFoot.current.l[1];
    
    const speedRHand = Math.sqrt((handR[0] - prevHand.current.r[0]) ** 2 + (handR[1] - prevHand.current.r[1]) ** 2);
    const speedLHand = Math.sqrt((handL[0] - prevHand.current.l[0]) ** 2 + (handL[1] - prevHand.current.l[1]) ** 2);
    const speedRFoot = Math.sqrt((footR[0] - prevFoot.current.r[0]) ** 2 + (footR[1] - prevFoot.current.r[1]) ** 2);
    const speedLFoot = Math.sqrt((footL[0] - prevFoot.current.l[0]) ** 2 + (footL[1] - prevFoot.current.l[1]) ** 2);
    
    prevHand.current = { r: handR, l: handL };
    prevFoot.current = { r: footR, l: footL };
    
    const now = Date.now();
    
    // Sistema de fases para mano derecha
    const updateStrikePhase = (limb, extension, speed, direction, isKick = false) => {
      const state = strikeState.current[limb];
      const minExtension = isKick ? 0.55 : 0.4;
      const minSpeed = isKick ? 0.018 : 0.010;
      const directionCheck = isKick ? direction < -0.005 : direction > 0.005;
      
      if (state.phase === 'idle') {
        if (extension > minExtension && speed > minSpeed && directionCheck) {
          state.phase = 'extending';
          state.startTime = now;
          state.extension = extension;
          state.direction = direction;
        }
      } else if (state.phase === 'extending') {
        if (now - state.startTime > 60 && extension > minExtension && speed > minSpeed * 0.5) {
          state.phase = 'impact';
        } else if (now - state.startTime > 500 || extension < minExtension * 0.5) {
          state.phase = 'idle';
        }
      } else if (state.phase === 'impact') {
        if (now - state.startTime > 500 || extension < minExtension * 0.4) {
          state.phase = 'cooldown';
          state.startTime = now;
        }
      } else if (state.phase === 'cooldown') {
        if (now - state.startTime > 400) {
          state.phase = 'idle';
        }
      }
    };
    
    updateStrikePhase('rightHand', rArmExtension, speedRHand, rHandDirX);
    updateStrikePhase('leftHand', lArmExtension, speedLHand, lHandDirX);
    updateStrikePhase('rightFoot', rLegExtension, speedRFoot, rFootDirY, true);
    updateStrikePhase('leftFoot', lLegExtension, speedLFoot, lFootDirY, true);
    
    // Detectar impacto solo en fase 'impact'
    let hitZone = null;
    let hitLimb = null;
    let hitPower = 0;
    
    // Verificar si golpeó algún círculo activo
    if (targetCirclesRef.current.length > 0) {
      targetCirclesRef.current.forEach((circle, index) => {
        const elapsed = Date.now() - circle.startTime;
        if (elapsed >= circle.duration) return; // Círculo expirado, no se puede golpear
        
        const zX = opponentXPos + (width * circle.xOffset);
        const zY = height * circle.y;
        const radius = circle.radius || 40;
        
        const rHandX = width * rIndex.x;
        const rHandY = height * rIndex.y;
        const lHandX = width * lIndex.x;
        const lHandY = height * lIndex.y;
        const rFootX = width * rFoot.x;
        const rFootY = height * rFoot.y;
        const lFootX = width * lFoot.x;
        const lFootY = height * lFoot.y;
        
        const distRHand = Math.sqrt((rHandX - zX) ** 2 + (rHandY - zY) ** 2);
        const distLHand = Math.sqrt((lHandX - zX) ** 2 + (lHandY - zY) ** 2);
        const distRFoot = Math.sqrt((rFootX - zX) ** 2 + (rFootY - zY) ** 2);
        const distLFoot = Math.sqrt((lFootX - zX) ** 2 + (lFootY - zY) ** 2);
        
        // Radio de impacto aumenta con habilidad (+0.5px por punto)
        const habilidadRadius = (userData?.habilidad_total || 10) * 0.5;
        const handHitRadius = radius + 25 + habilidadRadius;
        const footHitRadius = radius + 20 + habilidadRadius;
        
        if (strikeState.current.rightHand.phase === 'impact' && distRHand < handHitRadius) {
          hitZone = circle.name;
          hitLimb = 'rightHand';
          hitPower = speedRHand * rArmExtension;
          circle.hit = true;
        } else if (strikeState.current.leftHand.phase === 'impact' && distLHand < handHitRadius) {
          hitZone = circle.name;
          hitLimb = 'leftHand';
          hitPower = speedLHand * lArmExtension;
          circle.hit = true;
        } else if (strikeState.current.rightFoot.phase === 'impact' && distRFoot < footHitRadius) {
          hitZone = circle.name;
          hitLimb = 'rightFoot';
          hitPower = speedRFoot * rLegExtension * 1.5;
          circle.hit = true;
        } else if (strikeState.current.leftFoot.phase === 'impact' && distLFoot < footHitRadius) {
          hitZone = circle.name;
          hitLimb = 'leftFoot';
          hitPower = speedLFoot * lLegExtension * 1.5;
          circle.hit = true;
        }
      });
    }
    
    if (hitZone && hitLimb) {
      // Cooldown entre golpes reducido por técnica (-3ms por punto)
      const tecnicaCooldown = Math.max(150, 300 - (userData?.tecnica_total || 10) * 3);
      if (now - lastHitTime.current < tecnicaCooldown) return;
      lastHitTime.current = now;
      
      // Registrar ataque del jugador para la IA
      playerAttackState.current = { isAttacking: true, lastAttackTime: now };
      setTimeout(() => {
        playerAttackState.current.isAttacking = false;
      }, 500);
      
      strikeState.current[hitLimb].phase = 'cooldown';
      strikeState.current[hitLimb].startTime = now;
      
      if (opponentBlocking) {
        setFeedback({ show: true, text: '¡BLOQUEADO!', type: 'blocked' });
        if (opponentAI.current) {
          opponentAI.current.resetPlayerHitCounter();
        }
      } else {
        // Sistema de daño con stats totales (base + equipamiento)
        const fuerza = userData?.fuerza_total || 10;
        const tecnica = userData?.tecnica_total || 10;
        const habilidad = userData?.habilidad_total || 10;
        const isKick = hitLimb.includes('Foot');
        
        let damage;
        if (isKick) {
          damage = Math.round(5 + (fuerza * 0.12) + (habilidad * 0.15));
        } else {
          damage = Math.round(3 + (fuerza * 0.15) + (tecnica * 0.08));
        }
        
        // Penalización por stats bajos vs oponentes fuertes
        const maxHP = opponentMaxHealth.current || 100;
        const totalStats = fuerza + tecnica + habilidad + (userData?.defensa_total || 10) + (userData?.velocidad_total || 10);
        const expectedStats = maxHP * 0.8;
        if (totalStats < expectedStats) {
          const penalty = Math.max(0.4, totalStats / expectedStats);
          damage = Math.round(damage * penalty);
        }
        
        // Bonus de combo
        const currentCombo = comboCount;
        if (currentCombo >= 3) {
          damage = Math.round(damage * (1 + currentCombo * 0.08));
        }
        
        // Animación de daño
        setDamageAnimation(prev => ({ ...prev, opponent: damage }));
        setTimeout(() => setDamageAnimation(prev => ({ ...prev, opponent: null })), 1000);
        
        // Crear partículas en el punto de impacto
        const hitX = hitLimb.includes('Hand') ? 
          (hitLimb === 'rightHand' ? width * rIndex.x : width * lIndex.x) :
          (hitLimb === 'rightFoot' ? width * rFoot.x : width * lFoot.x);
        const hitY = hitLimb.includes('Hand') ? 
          (hitLimb === 'rightHand' ? height * rIndex.y : height * lIndex.y) :
          (hitLimb === 'rightFoot' ? height * rFoot.y : height * lFoot.y);
        
        createParticles(hitX, hitY, '#FFD700', 30);
        if (pegarOponenteRef.current) { pegarOponenteRef.current.currentTime = 0; pegarOponenteRef.current.play().catch(() => {}); }
        
        // Remover el círculo golpeado
        setTargetCircles(prev => {
          const newCircles = prev.filter(c => !c.hit);
          targetCirclesRef.current = newCircles;
          
          // Si completó todos, generar nueva secuencia con delay
          if (newCircles.length === 0) {
            clearInterval(circleTimerRef.current);
            setTimeout(() => generateTargetCircles(), 400);
          }
          
          return newCircles;
        });
        
        setOpponentHealth(h => Math.max(0, h - damage));
        
        // Notificar a la IA que recibió un golpe
        if (opponentAI.current) {
          opponentAI.current.onPlayerHit(now);
        }
        
        // Sistema de combos
        setComboCount(prev => {
          const newCombo = prev + 1;
          clearTimeout(comboTimer.current);
          comboTimer.current = setTimeout(() => setComboCount(0), 2000);
          
          if (newCombo >= 3) {
            setFeedback({ show: true, text: `¡COMBO x${newCombo}!`, type: 'combo' });
          } else {
            setFeedback({ show: true, text: '¡GOLPE!', type: 'hit' });
          }
          
          return newCombo;
        });
      }
      setHitEffect(true);
      setTimeout(() => {
        setHitEffect(false);
        setFeedback({ show: false, text: '', type: '' });
      }, 800);
    }
    
    // Detectar si esquivaste o bloqueaste el ataque
    if (attack.current.active) {
      const elapsed = Date.now() - attack.current.startTime;
      const progress = elapsed / 2000;
      
      if (progress > 0.75 && progress <= 1.0) {
        const noseY = nose.y;
        const attackX = attack.current.x;
        const noseX = nose.x;
        
        if (attack.current.type === 'foot') {
          // Ataque de patada: bloquear con manos arriba
          const rHandY = rIndex.y;
          const lHandY = lIndex.y;
          const minHandY = Math.min(rHandY, lHandY);
          const isBlocking = minHandY < 0.45 && Math.abs(noseX - attackX) < 0.35;
          
          // Velocidad da más tiempo para reaccionar (+4ms por punto)
          const velocidadWindow = (userData?.velocidad_total || 10) * 4;
          const blockThreshold = Math.max(0.75, 0.80 - velocidadWindow / 10000);
          
          if (isBlocking && progress > blockThreshold) {
            attack.current.active = false;
            setFeedback({ show: true, text: '¡BLOQUEADO!', type: 'defense' });
            setTimeout(() => setFeedback({ show: false, text: '', type: '' }), 600);
          } else if (progress > 0.90 && !isBlocking) {
            const baseDmg = opponentAI.current ? opponentAI.current.getDamage('foot') : 15;
            // Defensa reduce daño flat (-0.4 por punto)
            const defensa = userData?.defensa_total || 10;
            const damage = Math.max(2, Math.round(baseDmg - (defensa * 0.4)));
            playerHealthRef.current = Math.max(0, playerHealthRef.current - damage);
            setPlayerHealth(playerHealthRef.current);
            setDamageAnimation(prev => ({ ...prev, player: damage }));
            setTimeout(() => setDamageAnimation(prev => ({ ...prev, player: null })), 1000);
            setComboCount(0);
            attack.current.active = false;
            if (golpeOponenteRef.current) { golpeOponenteRef.current.currentTime = 0; golpeOponenteRef.current.play().catch(() => {}); }
            setHitEffect('damage');
            setTimeout(() => setHitEffect(false), 300);
          }
        } else {
          // Ataque de mano: esquivar agachándose o moviéndose
          const isDodging = noseY > 0.6 || Math.abs(noseX - attackX) > 0.2;
          
          // Velocidad da más tiempo para esquivar (+4ms por punto)
          const velocidadDodgeWindow = (userData?.velocidad_total || 10) * 4;
          const dodgeThreshold = Math.max(0.75, 0.80 - velocidadDodgeWindow / 10000);
          
          if (isDodging && progress > dodgeThreshold) {
            attack.current.active = false;
            setFeedback({ show: true, text: '¡ESQUIVADO!', type: 'defense' });
            setTimeout(() => setFeedback({ show: false, text: '', type: '' }), 600);
          } else if (progress > 0.90 && !isDodging) {
            const baseDmg = opponentAI.current ? opponentAI.current.getDamage('hand') : 10;
            // Defensa reduce daño flat (-0.4 por punto)
            const defensa = userData?.defensa_total || 10;
            const damage = Math.max(2, Math.round(baseDmg - (defensa * 0.4)));
            playerHealthRef.current = Math.max(0, playerHealthRef.current - damage);
            setPlayerHealth(playerHealthRef.current);
            setDamageAnimation(prev => ({ ...prev, player: damage }));
            setTimeout(() => setDamageAnimation(prev => ({ ...prev, player: null })), 1000);
            setComboCount(0);
            attack.current.active = false;
            if (golpeOponenteRef.current) { golpeOponenteRef.current.currentTime = 0; golpeOponenteRef.current.play().catch(() => {}); }
            setHitEffect('damage');
            setTimeout(() => setHitEffect(false), 300);
          }
        }
      }
    }
  };

  const onResults = (results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) return;
    
    const ctx = canvas.getContext('2d');
    
    // Verificar que el video tenga dimensiones válidas
    if (video.videoWidth === 0 || video.videoHeight === 0) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Dibujar imagen de fondo o fondo oscuro
    if (images.current.background.complete && images.current.background.naturalWidth > 0) {
      ctx.drawImage(images.current.background, 0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    // Dibujar oponente
    drawOpponent(ctx, canvas.width, canvas.height);
    
    // Dibujar ataque del oponente
    drawAttack(ctx, canvas.width, canvas.height);
    
    if (results.poseLandmarks) {
      // No dibujar esqueleto ni guantes durante agarre
      if (!grabRef.current.active) {
        // Dibujar esqueleto
        drawSkeleton(ctx, results.poseLandmarks, canvas.width, canvas.height);
        
        // Dibujar guantes
        drawGloves(ctx, results.poseLandmarks, canvas.width, canvas.height);
      }
      
      // Detectar golpes
      detectHit(results.poseLandmarks, canvas.width, canvas.height);
    }
    
    // Actualizar y dibujar partículas
    updateParticles();
    drawParticles(ctx);
    
    // Efecto de golpe mejorado
    if (hitEffect === true) {
      const hitGradient = ctx.createRadialGradient(canvas.width/2, canvas.height/2, 0, canvas.width/2, canvas.height/2, canvas.width/2);
      hitGradient.addColorStop(0, 'rgba(255, 255, 0, 0.4)');
      hitGradient.addColorStop(1, 'rgba(255, 255, 0, 0)');
      ctx.fillStyle = hitGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (hitEffect === 'damage') {
      ctx.fillStyle = 'rgba(255, 0, 0, 0.45)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    // Efecto de daño recibido mejorado
    if (damageAnimation.player) {
      const damageGradient = ctx.createRadialGradient(canvas.width/2, canvas.height/2, 0, canvas.width/2, canvas.height/2, canvas.width/2);
      damageGradient.addColorStop(0, 'rgba(255, 0, 0, 0.5)');
      damageGradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
      ctx.fillStyle = damageGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    // Feedback visual mejorado
    if (feedback.show) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.translate(-canvas.width, 0);
      
      const colors = {
        perfect: '#FFD700',
        combo: '#FF6B00',
        hit: '#00FF00',
        blocked: '#888888',
        defense: '#00BFFF',
        timeout: '#FF0000'
      };
      
      const feedbackTime = Date.now() % 1000;
      const scale = 1 + Math.sin(feedbackTime / 100) * 0.1;
      
      ctx.shadowColor = colors[feedback.type];
      ctx.shadowBlur = 40;
      ctx.fillStyle = colors[feedback.type];
      ctx.font = `bold ${48 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.strokeText(feedback.text, canvas.width / 2, canvas.height / 2 - 50);
      ctx.fillText(feedback.text, canvas.width / 2, canvas.height / 2 - 50);
      ctx.restore();
    }
    
    // Contador de combo
    if (comboCount >= 2) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.translate(-canvas.width, 0);
      ctx.fillStyle = '#FF6B00';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.shadowColor = '#FF6B00';
      ctx.shadowBlur = 20;
      ctx.fillText(`COMBO x${comboCount}`, canvas.width / 2, 150);
      ctx.restore();
    }

    ctx.restore();
  };

  // Generar círculos objetivo aleatorios ajustados a la dificultad
  const generateTargetCircles = () => {
    if (isGeneratingCircles.current) return;
    isGeneratingCircles.current = true;

    const ai = opponentAI.current;
    const aggressiveness = ai ? ai.getAggressiveness() : 0.3;

    // Más círculos y menos tiempo en dificultades altas
    const numCircles = aggressiveness >= 0.8 ? 3 + Math.floor(Math.random() * 2)
      : aggressiveness >= 0.5 ? 2 + Math.floor(Math.random() * 2)
      : 2;
    const baseDuration = aggressiveness >= 0.8 ? 3000
      : aggressiveness >= 0.5 ? 4000
      : 5000;

    const positions = [
      { y: 0.30, xOffset: -0.08, name: 'Cabeza Izq', radius: 35 },
      { y: 0.30, xOffset: 0.08, name: 'Cabeza Der', radius: 35 },
      { y: 0.42, xOffset: -0.12, name: 'Torso Izq', radius: 45 },
      { y: 0.42, xOffset: 0, name: 'Torso Centro', radius: 45 },
      { y: 0.42, xOffset: 0.12, name: 'Torso Der', radius: 45 },
      { y: 0.54, xOffset: -0.10, name: 'Abdomen Izq', radius: 40 },
      { y: 0.54, xOffset: 0.10, name: 'Abdomen Der', radius: 40 },
    ];
    const shuffled = [...positions].sort(() => Math.random() - 0.5);
    const now = Date.now();
    const circles = [];
    for (let i = 0; i < numCircles; i++) {
      circles.push({ ...shuffled[i], startTime: now, duration: baseDuration + (i * 1500), hit: false });
    }
    targetCirclesRef.current = circles;
    setTargetCircles(circles);

    // Pequeño delay para evitar doble generación
    setTimeout(() => { isGeneratingCircles.current = false; }, 300);
  };

  // Timer único que verifica expiración y regenera
  useEffect(() => {
    if (gameState !== 'playing' || !isReady) return;
    const timer = setInterval(() => {
      const circles = targetCirclesRef.current;
      if (!circles || circles.length === 0) {
        generateTargetCircles();
        return;
      }
      const now = Date.now();
      let hasExpired = false;
      const remaining = circles.filter(circle => {
        if (circle.hit) return false;
        if (now - circle.startTime >= circle.duration) { hasExpired = true; return false; }
        return true;
      });
      if (hasExpired) {
        setComboCount(0);
        targetCirclesRef.current = remaining;
        setTargetCircles(remaining);
        if (remaining.length === 0) {
          // Delay breve antes de regenerar para evitar conflictos
          setTimeout(() => generateTargetCircles(), 500);
        }
      }
    }, 100);
    return () => clearInterval(timer);
  }, [gameState, isReady]);

  useEffect(() => {
    playerHealthRef.current = playerHealth;
  }, [playerHealth]);

  useEffect(() => {
    opponentHealthRef.current = opponentHealth;
  }, [opponentHealth]);

  useEffect(() => {
    if (gameState !== 'playing' || !isReady) return;
    generateTargetCircles();
    return () => clearInterval(circleTimerRef.current);
  }, [gameState, isReady]);
  // Generar ataques del oponente con IA
  useEffect(() => {
    if (gameState !== 'playing' || !opponentAI.current) return;
    
    const isMartial = opponentData?.id?.startsWith('martial_');
    
    const attackInterval = setInterval(() => {
      const now = Date.now();
      
      // No atacar si hay agarre activo
      if (grabRef.current.active) return;
      
      // Intentar agarre (solo torneo marcial)
      if (isMartial && !attack.current.active && opponentAI.current.shouldGrab(now)) {
        opponentAI.current.onGrab(now);
        // Velocidad reduce sacudidas necesarias para escapar
        const velocidad = userData?.velocidad_total || 10;
        const shakeNeeded = Math.max(3, 6 - Math.floor(velocidad / 20));
        grabRef.current = { active: true, startTime: now, shakeCount: 0, shakeNeeded, lastDir: 0 };
        setIsGrabbed(true);
        setGrabEscapeProgress(0);
        
        // Timeout: si no escapa, recibe daño (defensa reduce daño del agarre)
        const escapeTime = opponentAI.current.config.grabEscapeTime || 2500;
        setTimeout(() => {
          if (grabRef.current.active) {
            const baseGrabDmg = opponentAI.current.config.grabDamage || 20;
            const defensa = userData?.defensa_total || 10;
            const grabDmg = Math.max(4, Math.round(baseGrabDmg - (defensa * 0.3)));
            playerHealthRef.current = Math.max(0, playerHealthRef.current - grabDmg);
            setPlayerHealth(playerHealthRef.current);
            setDamageAnimation(prev => ({ ...prev, player: grabDmg }));
            setTimeout(() => setDamageAnimation(prev => ({ ...prev, player: null })), 1000);
            grabRef.current.active = false;
            setIsGrabbed(false);
            if (golpeOponenteRef.current) { golpeOponenteRef.current.currentTime = 0; golpeOponenteRef.current.play().catch(() => {}); }
          }
        }, escapeTime);
        return;
      }
      
      if (!attack.current.active && opponentAI.current.shouldAttack(now)) {
        setOpponentX(currentX => {
          const attackPattern = opponentAI.current.selectAttackType();
          const attackType = attackPattern.includes('kick') ? 'foot' : 'hand';
          const variant = attackPattern.includes('2') ? 2 : 1;
          
          attack.current = {
            active: true,
            x: currentX,
            y: 0.3,
            size: 50,
            startTime: now,
            type: attackType,
            variant: variant
          };
          
          opponentAI.current.onAttack(now);
          return currentX;
        });
      }
    }, 500);

    return () => clearInterval(attackInterval);
  }, [gameState]);

  // Movimiento y defensa del oponente con IA
  useEffect(() => {
    console.log('useEffect movimiento iniciado, gameState:', gameState);
    if (gameState !== 'playing' || !opponentAI.current) return;
    
    // Movimiento continuo y fluido basado en IA
    const moveInterval = setInterval(() => {
      setOpponentX(prev => {
        const aggressiveness = opponentAI.current.getAggressiveness();
        
        // Cambio de dirección basado en agresividad
        const changeDirectionChance = 0.92 - (aggressiveness * 0.1);
        if (Math.random() > changeDirectionChance) {
          opponentDirection.current *= -1;
          opponentSpeed.current = opponentAI.current.getMovementSpeed();
        }
        
        let newX = prev + (opponentDirection.current * opponentSpeed.current);
        
        // Cambiar dirección en los bordes
        if (newX <= 0.2 || newX >= 0.8) {
          opponentDirection.current *= -1;
          newX = prev + (opponentDirection.current * opponentSpeed.current);
        }
        
        opponentXRef.current = newX;
        return Math.max(0.2, Math.min(0.8, newX));
      });
    }, 16);
    
    // Bloqueo inteligente basado en ataques del jugador
    const blockInterval = setInterval(() => {
      const now = Date.now();
      const isPlayerAttacking = playerAttackState.current.isAttacking;
      
      if (opponentAI.current.shouldBlock(isPlayerAttacking, now)) {
        setOpponentBlocking(true);
        console.log('Oponente bloquea!');
        setTimeout(() => setOpponentBlocking(false), 800);
      } else if (isPlayerAttacking && opponentAI.current.shouldDodge()) {
        // Esquiva rápida
        opponentDirection.current *= -1;
        opponentSpeed.current = opponentAI.current.getMovementSpeed() * 2;
        console.log('Oponente esquiva!');
      }
    }, 200);

    return () => {
      console.log('Limpiando intervalos de movimiento');
      clearInterval(moveInterval);
      clearInterval(blockInterval);
    };
  }, [gameState]);

  onResultsRef.current = onResults;
  const poseLoader = usePoseLoader((...args) => onResultsRef.current?.(...args));

  // Iniciar carga de Pose
  useEffect(() => {
    if (videoRef.current) poseLoader.start(videoRef.current);
    return () => {
      poseLoader.stop();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };
  }, []);

  // Cuando Pose termina de cargar, iniciar countdown y timer
  useEffect(() => {
    if (!poseLoader.loaded) return;
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
    let timer = null;
    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          setIsReady(true);
          timer = setInterval(() => {
            setTimeLeft((prev) => {
              const newTime = prev <= 1 ? 0 : prev - 1;
              timeLeftRef.current = newTime;
              if (newTime === 0 && !combatEndedRef.current) {
                combatEndedRef.current = true;
                setGameState('ended');
                const healthDiff = Math.abs(playerHealthRef.current - opponentHealthRef.current);
                if (healthDiff < 20) handleCombatEnd('empate');
                else if (playerHealthRef.current > opponentHealthRef.current) handleCombatEnd('victoria');
                else handleCombatEnd('derrota');
              }
              return newTime;
            });
          }, 1000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timer) clearInterval(timer); clearInterval(countdownTimer); };
  }, [poseLoader.loaded]);

  useEffect(() => {
    if (combatEndedRef.current) return;
    
    if (opponentHealth <= 0) {
      combatEndedRef.current = true;
      setGameState('won');
      handleCombatEnd('victoria');
    }
    if (playerHealth <= 0) {
      combatEndedRef.current = true;
      setGameState('lost');
      handleCombatEnd('derrota');
    }
  }, [opponentHealth, playerHealth]);

  const handleCombatEnd = async (resultado) => {
    // Sistema de XP: Victoria = 10, Empate = 3, Derrota = 1
    const xpRewards = {
      victoria: 10,
      empate: 3,
      derrota: 1
    };

    const rewards = {
      xp: xpRewards[resultado] || 2
    };

    // Enviar resultado al backend para actualizar XP
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/combat/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          resultado,
          xpGanado: rewards.xp,
          tiempo: 60 - timeLeftRef.current,
          precision: 0,
          score: 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Combate finalizado:', data);
        
        // Si es un desafío, enviar resultado
        if (challengeId) {
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/challenges/${challengeId}/result`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                score: 0,
                vidaRestante: playerHealthRef.current,
                tiempo: 60 - timeLeftRef.current
              })
            });
            console.log('Resultado de desafío enviado');
          } catch (chErr) {
            console.error('Error enviando resultado de desafío:', chErr);
          }
        }
        
        // Si subió de nivel, guardar datos del cinturón
        if (data.subiDeNivel && data.beltData) {
          setBeltPromotionData({
            cinturon: data.cinturon,
            beltData: data.beltData
          });
          console.log('Promoción de cinturón detectada:', data.cinturon);
        }
      }
    } catch (error) {
      console.error('Error al finalizar combate:', error);
    }

    setCombatResult({ resultado, rewards });
    
    // Si es torneo marcial y ganó, avanzar progreso en backend
    if (resultado === 'victoria' && opponentData?.id?.startsWith('martial_')) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_API_URL}/profile/martial-tournament/advance`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (e) {
        console.error('Error al avanzar torneo:', e);
      }
    }
    
    setTimeout(() => {
      setShowResultModal(true);
    }, 1000);
  };

  const handleCloseModal = () => {
    setShowResultModal(false);
    
    // Si hay promoción de cinturón, mostrarla después del modal de resultado
    if (beltPromotionData) {
      setTimeout(() => setShowBeltModal(true), 500);
    } else if (onBackToMenu) {
      onBackToMenu();
    }
  };

  const handleCloseBeltModal = () => {
    setShowBeltModal(false);
    setBeltPromotionData(null);
    if (onBackToMenu) {
      onBackToMenu();
    }
  };

  const handleRematch = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/combat/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          oponenteId: opponentData?.id,
          energiaCosto: opponentData?.energy || 15
        })
      });
      if (response.ok) {
        setShowResultModal(false);
        restart();
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.message || 'Energía insuficiente para revancha');
      }
    } catch (error) {
      console.error('Error al iniciar revancha:', error);
      alert('Error de conexión con el servidor');
    }
  };

  const restart = () => {
    combatEndedRef.current = false;
    setTimeLeft(60);
    timeLeftRef.current = 60;
    setGameState('playing');
    const initialHealth = opponentAI.current ? opponentAI.current.getInitialHealth() : 100;
    setOpponentHealth(initialHealth);
    setPlayerHealth(100);
    playerHealthRef.current = 100;
    opponentHealthRef.current = initialHealth;
    opponentMaxHealth.current = initialHealth;
    setOpponentX(0.5);
    setOpponentBlocking(false);
    setComboCount(0);
    clearTimeout(comboTimer.current);
    clearInterval(circleTimerRef.current);
    generateTargetCircles();
    opponentDirection.current = 1;
    opponentSpeed.current = 0.015;
    kickCount.current = 0;
  };

  return (
    <div className="App">

      <div className="game-container">
        <audio ref={audioRef} src={opponentData?.id?.startsWith('martial_') ? `/torneomartiarl/${MartialStyles[opponentData.martialIndex || 0].folder}/${MartialStyles[opponentData.martialIndex || 0].music}` : '/audio/musictkd.mp3'} loop />
        <audio ref={golpeOponenteRef} src="/audio/golpeoponente.mp3" preload="auto" />
        <audio ref={pegarOponenteRef} src="/audio/pegaroponente.mp3" preload="auto" />
        <video ref={videoRef} style={{ display: 'none' }} />
        <canvas ref={canvasRef} className="game-canvas" />
        
        {!poseLoader.loaded && (
          <PosePreloader progress={poseLoader.progress} statusText={poseLoader.statusText} title="COMBATE IA" hint="Dejá el celular en un lugar fijo sin que se caiga. Alejate hasta que te tome el cuerpo completo. Golpeá los círculos rojos. Cuando ataque con puño, movete al costado. Cuando ataque con patada, subí las manos." />
        )}
        
        {/* HUD del juego */}
        <button className="back-button" onClick={onBackToMenu}>
          ← VOLVER
        </button>
        
        <div className="game-hud">
          {/* Jugador - Izquierda */}
          <div className="player-hud">
            <div className="player-info">
              <div className="player-name">{userData?.name || 'TÚ'}</div>
              <div className="health-bar">
                <div className="health-fill player" style={{ width: `${playerHealth}%` }}></div>
                {damageAnimation.player && (
                  <div className="damage-indicator">-{damageAnimation.player}</div>
                )}
              </div>
            </div>
            <div className="player-avatar">
              {userData?.avatar ? (
                <img src={userData.avatar} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                '👤'
              )}
            </div>
          </div>
          
          {/* Reloj Central */}
          <div className={`game-timer ${timeLeft <= 10 ? 'warning' : ''}`}>
            {timeLeft}
          </div>
          
          {/* Oponente - Derecha */}
          <div className="opponent-hud">
            <div className="opponent-info">
              <div className="opponent-name">{opponentData?.name || 'OPONENTE'}</div>
              <div className="health-bar">
                <div className="health-fill opponent" style={{ width: `${(opponentHealth / opponentMaxHealth.current) * 100}%` }}></div>
                {damageAnimation.opponent && (
                  <div className="damage-indicator">-{damageAnimation.opponent}</div>
                )}
              </div>
            </div>
            <div className="opponent-avatar">
              {opponentData?.avatar ? (
                <img src={opponentData.avatar} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                '🤖'
              )}
            </div>
          </div>
        </div>

        {!isReady && countdown > 0 && (
          <div className="countdown-overlay">
            <div className="countdown-number">{countdown}</div>
            <div className="countdown-text">¡Prepárate!</div>
          </div>
        )}

        {!isReady && countdown === 0 && (
          <div className="countdown-overlay">
            <div className="countdown-go">¡PELEA!</div>
          </div>
        )}

        {isGrabbed && (
          <div className="grab-overlay">
            <div className="grab-message">¡AGARRE! Sacudí los brazos rápido</div>
            <div className="grab-bar">
              <div className="grab-fill" style={{ width: `${grabEscapeProgress}%` }}></div>
            </div>
            <div className="grab-hint">↔️ Mové los brazos de lado a lado</div>
          </div>
        )}

        {showResultModal && combatResult && (
          <CombatResultModal
            resultado={combatResult.resultado}
            rewards={combatResult.rewards}
            gender={userData?.genero || 'M'}
            onClose={handleCloseModal}
            onRematch={handleRematch}
          />
        )}

        {showBeltModal && beltPromotionData && (
          <BeltPromotionModal
            cinturon={beltPromotionData.cinturon}
            beltData={beltPromotionData.beltData}
            onClose={handleCloseBeltModal}
          />
        )}
      </div>
    </div>
  );
}

export default App;
